import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid2 as Grid,
  CircularProgress,
  Alert,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Controller } from 'react-hook-form';

import { AvatarShape, PageType, FontFamily, FontSize, BorderRadius } from '../../catalogs/pageCatalog.generated';
import DescribedField from './DescribedField';
import BlockList from './blocks/BlockList';
import TemplateSelector from './TemplateSelector';
import LogoUploadField from './LogoUploadField';
import { scaffoldProfessional, scaffoldCompany } from '../../utils/scaffoldDefaults';
import professionalsService from '../../services/professionalsService';

/**
 * SiteForm — Formulário do editor de sites multi-agenda.
 *
 * Organiza a entrada em 6 accordions:
 *   1. Identidade (SEO)
 *   2. Escopo (professional/places/schedules)
 *   3. Template (template choice + layoutMode)
 *   4. Estilos (colors, fonts, borders)
 *   5. Blocos (via BlockList)
 *
 * Props:
 *   @param {object} form — instância de useForm (RHF)
 *   @param {number} siteType — PageType enum int (1=LinkInBio, 2=Professional, 3=Company)
 *   @param {object[]} templates — templates disponíveis para templateId
 *   @param {object} context — contexto com availableProfessionals, availablePlaces, etc.
 *
 * O componente NÃO submete; apenas renderiza e controla via RHF.
 * Obrigação O11: cada campo tem descrição ≥1 parágrafo.
 */
export default function SiteForm({
  form,
  siteType = PageType.LinkInBio,
  templates = [],
  context = {},
  isEdit = false,
}) {
  const { control, watch } = form;
  const professionalId = watch('scope.professionalId');
  const placeId = watch('scope.placeId');

  const scopeReady =
    siteType === PageType.LinkInBio ||
    (siteType === PageType.Professional && !!professionalId) ||
    (siteType === PageType.Company && !!placeId);

  // Smart scaffolding: pré-preenche o formulário ao selecionar profissional (apenas criação)
  useEffect(() => {
    if (isEdit || !professionalId) return;
    if (siteType !== PageType.Professional && siteType !== PageType.LinkInBio) return;

    const professional = (context.availableProfessionals || []).find(p => p.id === professionalId);
    if (!professional) return;

    professionalsService.getPlacesSchedules(professionalId)
      .then(placesSchedules => {
        // getValues (não o `blocks` do watch) — dentro da promise o closure pode estar stale.
        const defaults = scaffoldProfessional(
          professional,
          siteType,
          placesSchedules,
          form.getValues('blocks')
        );
        Object.entries(defaults).forEach(([key, value]) => {
          form.setValue(key, value, { shouldDirty: true });
        });
      })
      .catch(err => console.warn('[SiteForm] scaffold professional failed:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalId]);

  // Smart scaffolding: pré-preenche o formulário ao selecionar place (apenas criação)
  useEffect(() => {
    if (isEdit || !placeId) return;
    if (siteType !== PageType.Company) return;

    const place = (context.availablePlaces || []).find(p => p.id === placeId);
    if (!place) return;

    const defaults = scaffoldCompany(place, form.getValues('blocks'));
    Object.entries(defaults).forEach(([key, value]) => {
      form.setValue(key, value, { shouldDirty: true });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);

  // Hook para gerenciar blocks — NOT USED DIRECTLY HERE, mas pode ser passado ao BlockList
  // O SiteForm apenas renderiza; o gerenciamento de blocks é via RHF Controller
  const blocks = watch('blocks') || [];

  const handleBlockUpdate = (blockId, newConfig) => {
    const idx = blocks.findIndex((b) => b.id === blockId);
    if (idx >= 0) {
      form.setValue(`blocks.${idx}.config`, newConfig, { shouldDirty: true });
    }
  };

  const handleBlockToggle = (blockId) => {
    const idx = blocks.findIndex((b) => b.id === blockId);
    if (idx >= 0) {
      form.setValue(`blocks.${idx}.enabled`, !blocks[idx].enabled, { shouldDirty: true });
    }
  };

  const handleBlockReorder = (fromIndex, toIndex) => {
    // Work on sorted order (same view as BlockList) then persist entire array
    const sorted = [...blocks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    form.setValue(
      'blocks',
      reordered.map((b, i) => ({ ...b, order: i })),
      { shouldDirty: true }
    );
  };

  const handleBlockAdd = (type) => {
    const newBlock = {
      id: crypto.randomUUID(), // UUID estável — nunca alterado (O8)
      type,
      enabled: true,
      order: blocks.length,
      config: {},
    };
    form.setValue('blocks', [...blocks, newBlock], { shouldDirty: true });
  };

  const handleBlockRemove = (blockId) => {
    const remaining = blocks.filter((b) => b.id !== blockId);
    form.setValue('blocks', remaining.map((b, i) => ({ ...b, order: i })), { shouldDirty: true });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {!scopeReady && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {siteType === PageType.Professional
            ? 'Selecione um profissional no Escopo para desbloquear o editor.'
            : 'Selecione um local no Escopo para desbloquear o editor.'}
        </Alert>
      )}

      {/* Accordion 1: Escopo — primeiro na lista */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Escopo
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {siteType === PageType.LinkInBio && (
            <Alert severity="info" icon={false}>
              Nenhuma configuração de escopo necessária. Blocos de agendamento e links são
              configurados diretamente na seção <strong>Blocos</strong>.
            </Alert>
          )}

          {siteType === PageType.Professional && (
            <Controller
              name="scope.professionalId"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DescribedField
                  label="Profissional"
                  description="Profissional base desta página. Define o contexto de todos os blocos."
                  error={!!error}
                  helperText={error?.message || 'Obrigatório para desbloquear o editor.'}
                >
                  <FormControl fullWidth size="small" error={!!error}>
                    <InputLabel>Profissional</InputLabel>
                    <Select {...field} value={field.value ?? ''} label="Profissional">
                      <MenuItem value=""><em>Selecione...</em></MenuItem>
                      {(context.availableProfessionals || []).map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </DescribedField>
              )}
            />
          )}

          {siteType === PageType.Company && (
            <Controller
              name="scope.placeId"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DescribedField
                  label="Local Principal"
                  description="Local base desta página. Locais adicionais podem ser configurados no bloco Locais."
                  error={!!error}
                  helperText={error?.message || 'Obrigatório para desbloquear o editor.'}
                >
                  <FormControl fullWidth size="small" error={!!error}>
                    <InputLabel>Local</InputLabel>
                    <Select {...field} value={field.value ?? ''} label="Local">
                      <MenuItem value=""><em>Selecione...</em></MenuItem>
                      {(context.availablePlaces || []).map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </DescribedField>
              )}
            />
          )}
        </AccordionDetails>
      </Accordion>

      {/* Accordion 2: Identidade (SEO) */}
      <Accordion disabled={!scopeReady}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Identidade
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="seo.title"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Título da Página"
                description="Título exibido no navegador e mecanismos de busca. Máx. 120 caracteres. Use o nome do profissional ou empresa."
                error={!!error}
                helperText={error?.message}
              >
                <TextField
                  {...field}
                  placeholder="ex: Dra. João Silva - Dermatologia"
                  fullWidth
                  size="small"
                  variant="outlined"
                  inputProps={{ maxLength: 120 }}
                />
              </DescribedField>
            )}
          />

          <Controller
            name="identity.avatarUrl"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Avatar / Logo"
                description="Imagem principal de identidade da página. Usada em todos os templates."
                error={!!error}
                helperText={error?.message}
              >
                <LogoUploadField logoUrl={field.value} onChange={field.onChange} />
              </DescribedField>
            )}
          />

          <Controller
            name="identity.avatarShape"
            control={control}
            render={({ field }) => (
              <DescribedField
                label="Formato do Avatar"
                description="Circular para retratos; livre para logos horizontais ou retangulares."
              >
                <ToggleButtonGroup
                  value={field.value}
                  exclusive
                  onChange={(_, v) => v !== null && field.onChange(v)}
                  size="small"
                >
                  <ToggleButton value={AvatarShape.Round}>Circular</ToggleButton>
                  <ToggleButton value={AvatarShape.Free}>Livre</ToggleButton>
                </ToggleButtonGroup>
              </DescribedField>
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* Accordion 3: Template */}
      <Accordion disabled={!scopeReady}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Template & Layout
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="templateId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TemplateSelector
                templates={templates}
                value={field.value}
                onChange={field.onChange}
                error={error?.message}
              />
            )}
          />

        </AccordionDetails>
      </Accordion>

      {/* Accordion 4: Estilos */}
      <Accordion disabled={!scopeReady}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Estilos
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={1}>
            {[
              { name: 'customStyles.primaryColor', label: 'Cor Primária', default: '#1976d2' },
              { name: 'customStyles.backgroundColor', label: 'Cor de Fundo', default: '#ffffff' },
              { name: 'customStyles.textColor', label: 'Cor de Texto', default: '#212121' },
              { name: 'customStyles.accentColor', label: 'Cor de Destaque', default: '#f50057' },
              { name: 'customStyles.buttonBackground', label: 'Fundo dos Botões', default: 'transparent' },
            ].map(({ name, label, default: dflt }) => (
              <Grid key={name} size={{ xs: 12, sm: 6 }}>
                <Controller
                  name={name}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={label}
                      type="color"
                      fullWidth
                      size="small"
                      variant="outlined"
                      error={!!error}
                      helperText={error?.message || `Padrão: ${dflt}`}
                      inputProps={{ title: label }}
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>

          <Controller
            name="customStyles.fontFamily"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Família de Fontes"
                description="Tipografia geral usada em toda a página."
                error={!!error}
                helperText={error?.message}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Fonte</InputLabel>
                  <Select {...field} label="Fonte">
                    <MenuItem value={FontFamily.Inter}>Inter</MenuItem>
                    <MenuItem value={FontFamily.Roboto}>Roboto</MenuItem>
                    <MenuItem value={FontFamily.Poppins}>Poppins</MenuItem>
                    <MenuItem value={FontFamily.Montserrat}>Montserrat</MenuItem>
                  </Select>
                </FormControl>
              </DescribedField>
            )}
          />

          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="customStyles.fontSize"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DescribedField
                    label="Tamanho de Fonte"
                    description="Escala geral de texto da página."
                    error={!!error}
                    helperText={error?.message}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Tamanho</InputLabel>
                      <Select {...field} label="Tamanho">
                        <MenuItem value={FontSize.Small}>Pequeno</MenuItem>
                        <MenuItem value={FontSize.Medium}>Médio</MenuItem>
                        <MenuItem value={FontSize.Large}>Grande</MenuItem>
                      </Select>
                    </FormControl>
                  </DescribedField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="customStyles.borderRadius"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DescribedField
                    label="Arredondamento"
                    description="Bordas dos elementos (cards, botões, etc)."
                    error={!!error}
                    helperText={error?.message}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Raio</InputLabel>
                      <Select {...field} label="Raio">
                        <MenuItem value={BorderRadius.None}>Nenhum (Quadrado)</MenuItem>
                        <MenuItem value={BorderRadius.Soft}>Suave</MenuItem>
                        <MenuItem value={BorderRadius.Round}>Redondo</MenuItem>
                      </Select>
                    </FormControl>
                  </DescribedField>
                )}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Accordion 5: Blocos */}
      <Accordion disabled={!scopeReady}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Blocos ({blocks.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <BlockList
            blocks={blocks}
            siteType={siteType}
            onUpdate={handleBlockUpdate}
            onToggle={handleBlockToggle}
            onReorder={handleBlockReorder}
            onAdd={handleBlockAdd}
            onRemove={handleBlockRemove}
            context={context}
          />
        </AccordionDetails>
      </Accordion>

      {/* Accordion 6: Avançado (SEO secundário) */}
      <Accordion disabled={!scopeReady}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={600}>
            Avançado
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="seo.description"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Descrição SEO"
                description="Texto exibido nos resultados de busca e redes sociais. Máx. 300 caracteres. Resuma a proposta da página."
                error={!!error}
                helperText={error?.message}
              >
                <TextField
                  {...field}
                  placeholder="ex: Consultório especializado em dermatologia clínica e estética."
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  variant="outlined"
                  inputProps={{ maxLength: 300 }}
                />
              </DescribedField>
            )}
          />

          <Controller
            name="seo.ogImageUrl"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DescribedField
                label="Imagem OG (URL)"
                description="URL da imagem exibida ao compartilhar a página em redes sociais. Deixe vazio para usar imagem padrão."
                error={!!error}
                helperText={error?.message}
              >
                <TextField
                  {...field}
                  placeholder="https://..."
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </DescribedField>
            )}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
